/* ============================================================
   TAURR home page behaviour
   - Lenis smooth scrolling on Horizon's `.page-wrapper` scroll container
   - Gesture-driven "Choose your flavor" section: the section locks the
     page and ONE wheel flick / swipe / arrow key plays ONE complete
     flavor transition (fullpage style, GSAP Observer) — no scrubbing.
   - "How are we different" carousel, header drawer, CTA play, reveals

   NOTE on scrolling: Horizon's squeeze layout makes `.page-wrapper` the
   scroll container on desktop (>=990px) and the document on mobile, so
   ScrollTriggers are created per-breakpoint with the right scroller.
   ============================================================ */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';

  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger);
  }

  var SQUEEZE = '(min-width: 990px)';

  /* ---------------- money ----------------
     Formats cents with the STORE's money format (window.TAURR.moneyFormat,
     emitted by snippets/taurr-base.liquid), so JS-rendered prices match the
     ones Liquid and checkout render. Supports the Shopify placeholder set:
     amount, amount_no_decimals, and the comma / period / apostrophe / space
     separator variants. */
  function groupDigits(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function amountFor(cents, decimals, thousands, decimalMark) {
    var n = Math.abs(Number(cents) || 0) / 100;
    var fixed = n.toFixed(decimals);
    var parts = fixed.split('.');
    var out = groupDigits(parts[0], thousands);
    if (decimals > 0 && parts[1]) out += decimalMark + parts[1];
    return (Number(cents) < 0 ? '-' : '') + out;
  }

  function formatMoney(cents) {
    var fmt = (window.TAURR && window.TAURR.moneyFormat) || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':                                return amountFor(cents, 2, ',', '.');
        case 'amount_no_decimals':                    return amountFor(cents, 0, ',', '.');
        case 'amount_with_comma_separator':           return amountFor(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return amountFor(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':      return amountFor(cents, 2, "'", '.');
        case 'amount_with_space_separator':           return amountFor(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return amountFor(cents, 0, ' ', ',');
        case 'amount_with_period_and_space_separator': return amountFor(cents, 2, ' ', '.');
        default:                                      return amountFor(cents, 2, ',', '.');
      }
    });
  }

  function escHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getScroller() {
    if (window.matchMedia(SQUEEZE).matches) {
      return document.querySelector('.page-wrapper') || window;
    }
    return window;
  }

  function scrollerTop(scroller) {
    return scroller === window ? (window.scrollY || 0) : scroller.scrollTop;
  }

  /* ---------------- Lenis smooth scrolling ----------------
     Lenis needs a single content element inside the scroll wrapper, so the
     wrapper's children are moved into one flex column mirroring its layout. */
  var lenis = null;
  (function initLenis() {
    if (typeof window.Lenis === 'undefined' || !hasGsap || prefersReduced) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (!window.matchMedia(SQUEEZE).matches) return;
    var pw = document.querySelector('.page-wrapper');
    if (!pw) return;

    function start() {
      var content = document.createElement('div');
      content.className = 'tr-lenis-content';
      while (pw.firstChild) content.appendChild(pw.firstChild);
      pw.appendChild(content);
      lenis = new Lenis({ wrapper: pw, content: content, lerp: 0.105, wheelMultiplier: 1 });
      window.taurrLenis = lenis;
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  })();

  function scrollerToY(scroller, y, opts) {
    opts = opts || {};
    if (lenis && scroller !== window) {
      lenis.scrollTo(y, {
        duration: opts.duration || 1,
        force: true,
        easing: function (t) { return 1 - Math.pow(1 - t, 3); },
      });
      return;
    }
    var target = scroller === window ? window : scroller;
    target.scrollTo({ top: y, behavior: prefersReduced ? 'auto' : 'smooth' });
  }

  /* ---------------- Age gate ----------------
     The overlay is already on screen (CSS shows it unless <html> carries
     .tr-age-ok). This only wires the two buttons and moves focus into it. */
  function initAgeGate(root) {
    var gate = root.querySelector('[data-tr-agegate]');
    if (!gate || gate.dataset.trInit) return;
    gate.dataset.trInit = '1';

    /* Same hoist as the cart drawer — a full-screen overlay should not live
       inside the scroll container it is covering. */
    if (gate.parentElement !== document.body) document.body.appendChild(gate);

    var minAge = gate.dataset.trMinAge || '18';
    var declineUrl = gate.dataset.trDeclineUrl || 'https://www.google.com';

    /* In the theme editor the merchant needs to see the section, but must not
       be locked out of the rest of the preview by their own gate. */
    if (window.Shopify && window.Shopify.designMode) {
      document.documentElement.classList.add('tr-age-ok');
      return;
    }

    if (document.documentElement.classList.contains('tr-age-ok')) return;

    var yes = gate.querySelector('[data-tr-agegate-yes]');
    var no = gate.querySelector('[data-tr-agegate-no]');

    if (yes) {
      yes.addEventListener('click', function () {
        try { localStorage.setItem('taurr-age-ok', minAge); } catch (e) {}
        document.documentElement.classList.add('tr-age-ok');
      });
      yes.focus();
    }
    if (no) {
      no.addEventListener('click', function () { window.location.href = declineUrl; });
    }

    /* Keep Tab inside the gate — nothing behind it should be reachable. */
    gate.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = [yes, no].filter(Boolean);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
  }

  /* ---------------- Cookie consent ----------------
     Hidden in markup, revealed here only for a visitor who has not answered.
     The answer is written through Shopify's Customer Privacy API so it gates
     the pixels themselves; localStorage only stops us re-asking. */
  function initCookieConsent(root) {
    var bar = root.querySelector('[data-tr-cookie]');
    if (!bar || bar.dataset.trInit) return;
    bar.dataset.trInit = '1';

    /* Same hoist as the cart drawer and age gate: a fixed bar must not sit
       inside .page-wrapper, which is the scroll container on desktop. */
    if (bar.parentElement !== document.body) document.body.appendChild(bar);

    /* In the theme editor the merchant needs to see the bar they are editing. */
    if (window.Shopify && window.Shopify.designMode) { bar.hidden = false; return; }

    var KEY = 'taurr-cookie-consent';
    var stored = null;
    try { stored = localStorage.getItem(KEY); } catch (e) {}

    function record(accepted) {
      try { localStorage.setItem(KEY, accepted ? 'all' : 'essential'); } catch (e) {}
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && api.setTrackingConsent) {
        api.setTrackingConsent({
          analytics: accepted, marketing: accepted,
          preferences: accepted, sale_of_data: accepted
        }, function () {});
      }
      bar.hidden = true;
    }

    var accept = bar.querySelector('[data-tr-cookie-accept]');
    var decline = bar.querySelector('[data-tr-cookie-decline]');
    if (accept) accept.addEventListener('click', function () { record(true); });
    if (decline) decline.addEventListener('click', function () { record(false); });

    if (stored) return;

    /* Wait out the age gate — two stacked overlays on a first visit is a wall,
       and the age answer has to come first anyway. */
    function reveal() { bar.hidden = false; }
    var gated = !!document.querySelector('[data-tr-agegate]');
    if (!gated || document.documentElement.classList.contains('tr-age-ok')) {
      reveal();
      return;
    }
    var watcher = new MutationObserver(function () {
      if (document.documentElement.classList.contains('tr-age-ok')) {
        watcher.disconnect();
        reveal();
      }
    });
    watcher.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------- Header ---------------- */
  function initHeader(root) {
    var header = root.querySelector('[data-tr-header]');
    if (!header || header.dataset.trInit) return;
    header.dataset.trInit = '1';
    var burger = header.querySelector('[data-tr-burger]');
    if (burger) {
      burger.addEventListener('click', function () {
        header.classList.toggle('is-open');
        burger.setAttribute('aria-expanded', header.classList.contains('is-open') ? 'true' : 'false');
      });
    }

    /* Sticky-on-scroll-up: the in-flow header stays pixel-perfect at the
       top; once scrolled past, scrolling UP slides in a fixed light bar. */
    var group = header.closest('.shopify-section') || header.parentElement;
    var fixed = false;
    var lastY = 0;
    var releaseAt = 0;

    function onScroll(y) {
      var headerH = header.offsetHeight;
      if (!fixed && y > headerH + 400) {
        fixed = true;
        releaseAt = headerH + 50;
        if (group) group.style.minHeight = group.offsetHeight + 'px';
        header.classList.add('is-fixed');
      } else if (fixed && y <= releaseAt) {
        fixed = false;
        header.classList.remove('is-fixed', 'is-shown');
        if (group) group.style.minHeight = '';
      }
      if (fixed) {
        if (y < lastY - 2) header.classList.add('is-shown');
        else if (y > lastY + 2) { header.classList.remove('is-shown'); header.classList.remove('is-open'); }
      }
      lastY = y;
    }
    var pw = document.querySelector('.page-wrapper');
    if (pw) pw.addEventListener('scroll', function () { onScroll(pw.scrollTop); }, { passive: true });
    window.addEventListener('scroll', function () { onScroll(window.scrollY || 0); }, { passive: true });
    /* Lenis writes scrollTop from its own raf; listen to it directly so the
       sticky logic never lags the smoothed scroll */
    function hookLenis() {
      if (window.taurrLenis) {
        window.taurrLenis.on('scroll', function (l) { onScroll(l.scroll); });
        return true;
      }
      return false;
    }
    if (!hookLenis()) setTimeout(hookLenis, 300);
  }

  /* ---------------- Hero intro ---------------- */
  function initHero(root) {
    var hero = root.querySelector('.tr-hero');
    if (!hero || hero.dataset.trInit || !hasGsap || prefersReduced) return;
    /* mobile: no intro animation — content must never depend on JS to appear */
    if (window.matchMedia('(max-width: 989px)').matches) return;
    hero.dataset.trInit = '1';
    var title = hero.querySelector('.tr-hero__title');
    var buttons = hero.querySelector('.tr-hero__buttons');
    var media = hero.querySelector('.tr-hero__media');
    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    if (media) tl.fromTo(media, { scale: 1.08 }, { scale: 1, duration: 1.8, ease: 'power2.out' }, 0);
    if (title) tl.fromTo(title, { y: 44, opacity: 0 }, { y: 0, opacity: 1, duration: 1 }, 0.15);
    if (buttons) tl.fromTo(buttons.children, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, stagger: 0.1 }, 0.45);
  }

  /* ---------------- Flavors: gesture-driven full-screen section ---------------- */
  function initFlavors(root) {
    var section = root.querySelector('[data-tr-flavors]');
    if (!section || section.dataset.trInit) return;
    section.dataset.trInit = '1';

    var stage = section.querySelector('[data-tr-stage]');
    var svg = section.querySelector('[data-tr-cardsvg]');
    var maskRect = section.querySelector('[data-tr-mask-rect]');
    var tintRect = section.querySelector('[data-tr-tint-rect]');
    var maskTexts = [].slice.call(section.querySelectorAll('[data-tr-mask-text]'));
    var videos = [].slice.call(section.querySelectorAll('[data-tr-video]'));
    var products = [].slice.call(section.querySelectorAll('[data-tr-product]'));
    var descs = [].slice.call(section.querySelectorAll('[data-tr-desc]'));
    var navItems = [].slice.call(section.querySelectorAll('[data-tr-navitem]'));
    var buyBtn = section.querySelector('[data-tr-buy]');
    var count = products.length;
    if (!count) return;

    var tints = maskTexts.map(function (t) { return t.getAttribute('data-tint') || '#CDF2FF'; });
    var buyLinks = maskTexts.map(function (t) { return t.getAttribute('data-url') || '#'; });
    var darkText = maskTexts.map(function (t) { return t.getAttribute('data-dark') === 'true'; });

    /* Size the knockout SVG to real pixels so radius / font stay true */
    function layoutSvg() {
      if (!svg) return;
      var w = svg.clientWidth || 1;
      var h = svg.clientHeight || 1;
      svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      var small = w < 700;
      var fs = small ? Math.max(48, w * 0.165) : Math.max(56, w * (150 / 1220));
      var rx = small ? 10 : Math.max(10, Math.min(40, w * (20 / 1220)));
      var cy = h * (small ? 0.4 : 0.303); /* text block centre per Figma card */
      maskTexts.forEach(function (t) {
        t.setAttribute('x', w / 2);
        t.setAttribute('y', cy + fs * 0.36); /* optical centring for Bebas caps */
        t.setAttribute('font-size', fs);
      });
      [maskRect, tintRect].forEach(function (r) {
        if (!r) return;
        r.setAttribute('width', w);
        r.setAttribute('height', h);
        r.setAttribute('rx', rx);
      });
      var fo = svg.querySelector('foreignObject');
      if (fo) {
        fo.setAttribute('width', w);
        fo.setAttribute('height', h);
      }
    }
    layoutSvg();
    window.addEventListener('resize', layoutSvg);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layoutSvg);

    var index = 0;
    var animating = false;
    var engaged = false;
    var vh = function () { return stage.clientHeight || window.innerHeight; };

    var mdots = [].slice.call(section.querySelectorAll('.tr-flavors__mdots span'));

    /* Non-active flavor videos ship with their URL in data-src so they aren't
       downloaded on page load. Attach the source the first time a flavor is
       shown, and warm the next one so the swap isn't visible. */
    /* Attaches the source without forcing a download — the element still
       carries preload="none", so bytes move only once play() is called. */
    function attachSrc(i) {
      var v = videos[i];
      if (!v || !v.dataset.src) return;
      v.src = v.dataset.src;
      delete v.dataset.src;
    }

    function playVideo(i) {
      var v = videos[i];
      if (!v) return;
      attachSrc(i);
      var p = v.play();
      if (p && p.catch) p.catch(function () {}); /* autoplay policy — poster stands in */
    }

    function markActive(i) {
      playVideo(i);
      /* Warm the next clip on desktop so the swap is seamless. Skipped on
         phones, where the saved megabytes matter more than the seam. */
      if (window.matchMedia(SQUEEZE).matches) {
        var nxt = videos[i + 1 < videos.length ? i + 1 : 0];
        if (nxt && nxt.dataset.src) { nxt.preload = 'auto'; attachSrc(i + 1 < videos.length ? i + 1 : 0); }
      }
      navItems.forEach(function (item, idx) {
        item.classList.toggle('is-active', idx === i);
      });
      /* mobile pill row scrolls horizontally — keep the active pill centred */
      var nav = section.querySelector('.tr-flavors__nav');
      if (nav && nav.scrollWidth > nav.clientWidth && navItems[i]) {
        var it = navItems[i];
        nav.scrollTo({
          left: it.offsetLeft - (nav.clientWidth - it.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
      mdots.forEach(function (d, idx) {
        d.classList.toggle('is-active', idx === i);
      });
      if (buyBtn) buyBtn.setAttribute('href', buyLinks[i]);
      section.classList.toggle('is-darktext', !!darkText[i]);
      var mobName = section.querySelector('[data-tr-mobname]');
      if (mobName) {
        var label = navItems[i] ? navItems[i].querySelector('.tr-flavors__navlabel') : null;
        if (label) mobName.textContent = label.textContent.trim();
      }
      videos.forEach(function (v, idx) {
        if (idx === i) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        }
      });
    }

    function showStatic(i) {
      videos.forEach(function (v, idx) { v.style.opacity = idx === i ? 1 : 0; });
      products.forEach(function (p, idx) {
        p.style.opacity = idx === i ? 1 : 0;
        p.style.transform = 'none';
      });
      descs.forEach(function (d, idx) { d.style.opacity = idx === i ? 1 : 0; });
      maskTexts.forEach(function (t, idx) { t.style.opacity = idx === i ? 1 : 0; });
      if (tintRect) tintRect.style.fill = tints[i];
      index = i;
      markActive(i);
    }

    if (!hasGsap || prefersReduced || (window.Shopify && window.Shopify.designMode)) {
      showStatic(0);
      navItems.forEach(function (item, idx) {
        item.addEventListener('click', function () { showStatic(idx); });
      });
      return;
    }

    /* Initial GSAP state */
    videos.forEach(function (v, i) { gsap.set(v, { opacity: i === 0 ? 1 : 0 }); });
    products.forEach(function (p, i) { gsap.set(p, { opacity: i === 0 ? 1 : 0, y: 0 }); });
    descs.forEach(function (d, i) { gsap.set(d, { opacity: i === 0 ? 1 : 0 }); });
    maskTexts.forEach(function (t, i) { gsap.set(t, { opacity: i === 0 ? 1 : 0, y: 0 }); });
    if (tintRect) gsap.set(tintRect, { fill: tints[0] });
    markActive(0);

    /* One complete transition, fixed duration — independent of scroll amount.
       dir: +1 = forward (next flavor rises from below), -1 = backward. */
    function transitionTo(next, dir) {
      if (animating || next < 0 || next > count - 1 || next === index) return false;
      animating = true;
      var cur = index;
      index = next;
      markActive(next);
      /* desktop transitions travel vertically (scroll-driven feel);
         mobile is a swipe slider, so everything travels horizontally */
      var horiz = window.matchMedia('(max-width: 989px)').matches;
      var axis = horiz ? 'x' : 'y';
      var off = (horiz ? (stage.clientWidth || window.innerWidth) * 0.8 : vh() * 0.72) * dir;
      function vars(base, offset) {
        base[axis] = offset;
        base[axis === 'x' ? 'y' : 'x'] = 0;
        return base;
      }
      var tl = gsap.timeline({
        onComplete: function () { animating = false; },
      });
      tl.to(products[cur], vars({ opacity: 0, duration: 0.6, ease: 'power2.in' }, -off), 0);
      tl.fromTo(products[next], vars({ opacity: 1 }, off), vars({ opacity: 1, duration: 0.85, ease: 'power3.out' }, 0), 0.32);
      tl.to(videos[cur], { opacity: 0, duration: 0.7, ease: 'power1.inOut' }, 0.1);
      tl.to(videos[next], { opacity: 1, duration: 0.7, ease: 'power1.inOut' }, 0.1);
      if (tintRect) tl.to(tintRect, { fill: tints[next], duration: 0.7, ease: 'power1.inOut' }, 0.1);
      tl.to(maskTexts[cur], vars({ opacity: 0, duration: 0.45, ease: 'power2.in' }, -64 * dir), 0);
      tl.fromTo(maskTexts[next], vars({ opacity: 0 }, 64 * dir), vars({ opacity: 1, duration: 0.6, ease: 'power2.out' }, 0), 0.35);
      tl.to(descs[cur], vars({ opacity: 0, duration: 0.3, ease: 'power1.in' }, -12 * dir), 0.05);
      tl.fromTo(descs[next], vars({ opacity: 0 }, 12 * dir), vars({ opacity: 1, duration: 0.4, ease: 'power1.out' }, 0), 0.5);
      return true;
    }

    /* -------- scroll locking -------- */
    function sectionTopY(scroller) {
      return section.getBoundingClientRect().top + scrollerTop(scroller);
    }

    function lockScroll() {
      if (lenis) {
        lenis.stop();
      } else {
        document.documentElement.style.overflow = 'hidden';
      }
    }
    function unlockScroll() {
      if (lenis) {
        lenis.start();
      } else {
        document.documentElement.style.overflow = '';
      }
    }

    var aligning = false;
    var alignedAt = 0;

    function step(dir) {
      if (!engaged || animating || aligning) return;
      if (performance.now() - alignedAt < 250) return;
      var next = index + dir;
      if (next < 0 || next > count - 1) {
        release(dir);
        return;
      }
      transitionTo(next, dir);
    }

    /* One gesture = one transition. A wheel flick fires immediately; the
       trailing inertia events are swallowed so a trackpad flick can't
       double-skip. Re-arm on a pause in the stream, OR on a strong
       non-decaying delta once the transition has finished — inertia tails
       decay monotonically, a held/renewed scroll doesn't, so continuous
       scrolling steps through flavors at animation pace instead of
       stalling until a full stop. */
    var lastWheelTime = 0;
    var lastWheelAbs = 0;
    var wheelArmed = true;
    function onWheel(e) {
      if (!engaged) return;
      e.preventDefault();
      var now = performance.now();
      var dt = now - lastWheelTime;
      lastWheelTime = now;
      var abs = Math.abs(e.deltaY);
      var sustained = abs >= 40 && abs >= lastWheelAbs;
      lastWheelAbs = abs;
      if (animating) { wheelArmed = false; return; }
      if (!wheelArmed) {
        if (dt > 160 || sustained) { wheelArmed = true; } else { return; }
      }
      if (abs < 4) return;
      wheelArmed = false;
      step(e.deltaY > 0 ? 1 : -1);
    }

    var touchStartY = null;
    var touchConsumed = false;
    function onTouchStart(e) {
      touchStartY = e.touches[0].clientY;
      touchConsumed = false;
    }
    function onTouchMove(e) {
      if (!engaged) return;
      e.preventDefault();
      if (touchConsumed || animating || touchStartY === null) return;
      var dy = touchStartY - e.touches[0].clientY;
      if (Math.abs(dy) > 45) {
        touchConsumed = true;
        step(dy > 0 ? 1 : -1);
      }
    }
    function onTouchEnd() {
      touchStartY = null;
    }

    function onKey(e) {
      if (!engaged || animating) return;
      /* These listeners are on window, so a keystroke meant for a field, a
         details/summary, or an open dialog must not be stolen and preventDefault'd. */
      var t = e.target;
      if (t && (t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')
                || t.closest('dialog, [role="dialog"], .tr-cartdrawer.is-open, .tr-agegate'))) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); step(-1); }
    }

    function addGestureListeners() {
      window.addEventListener('wheel', onWheel, { passive: false });
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd, { passive: true });
      window.addEventListener('keydown', onKey, { passive: false });
    }
    function removeGestureListeners() {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
    }

    function engage() {
      if (engaged) return;
      engaged = true;
      /* the gesture that brought the user here must NOT advance the flavors:
         block steps while aligning and swallow the ongoing wheel stream */
      aligning = true;
      wheelArmed = false;
      lastWheelTime = performance.now();
      addGestureListeners();
      /* keep the sticky header away for the whole takeover — the align
         scroll below moves up, which would otherwise re-trigger it */
      document.body.classList.add('tr-flavors-engaged');
      var stickyHeader = document.querySelector('.tr-header.is-fixed');
      if (stickyHeader) stickyHeader.classList.remove('is-shown');

      var scroller = getScroller();
      var top = sectionTopY(scroller);
      var doneTimer = setTimeout(finishAlign, 900); /* safety: never stay stuck aligning */
      function finishAlign() {
        clearTimeout(doneTimer);
        if (!engaged) return;
        aligning = false;
        alignedAt = performance.now();
      }

      if (lenis && scroller !== window) {
        lockScroll();
        lenis.scrollTo(top, {
          duration: 0.45,
          force: true,
          easing: function (t) { return 1 - Math.pow(1 - t, 2); },
          onComplete: finishAlign,
        });
      } else {
        /* window scroller (mobile): align FIRST, lock only once settled —
           locking early froze the smooth scroll and left the page stuck */
        var target = scroller === window ? window : scroller;
        target.scrollTo({ top: top, behavior: prefersReduced ? 'auto' : 'smooth' });
        var tries = 0;
        var poll = setInterval(function () {
          var y = scrollerTop(scroller);
          tries++;
          if (Math.abs(y - top) < 2 || tries > 16 || !engaged) {
            clearInterval(poll);
            if (engaged) {
              target.scrollTo({ top: top, behavior: 'auto' });
              lockScroll();
            }
            finishAlign();
          }
        }, 50);
      }
    }

    function forceDisengage() {
      engaged = false;
      removeGestureListeners();
      document.body.classList.remove('tr-flavors-engaged');
      unlockScroll();
    }

    function release(dir) {
      if (!engaged) return;
      engaged = false;
      removeGestureListeners();
      document.body.classList.remove('tr-flavors-engaged');
      unlockScroll();
      /* hand the user onward with momentum in their direction */
      var scroller = getScroller();
      var top = sectionTopY(scroller);
      var target = dir > 0 ? top + vh() : Math.max(0, top - vh() * 0.85);
      scrollerToY(scroller, target, { duration: 0.9 });
    }

    /* Engage when the section fills the viewport (entering from either side) */
    var mm = gsap.matchMedia();
    function buildTriggers(scroller) {
      var st = ScrollTrigger.create({
        trigger: section,
        scroller: scroller,
        start: 'top 55%',
        end: 'bottom 45%',
        onEnter: function () { engage(); },
        onEnterBack: function () { engage(); },
        /* safety: if the section leaves the viewport while locked (e.g.
           scrollbar drag bypassing the lock), disengage cleanly */
        onLeave: function () { if (engaged) forceDisengage(); },
        onLeaveBack: function () { if (engaged) forceDisengage(); },
      });
      return function () { st.kill(); };
    }
    mm.add(SQUEEZE, function () {
      return buildTriggers(document.querySelector('.page-wrapper') || window);
    });
    /* mobile (client request): NO scroll takeover — horizontal swipe slider;
       vertical scrolling stays native */
    mm.add('(max-width: 989px)', function () {
      var sx = 0, sy = 0, consumed = false;
      function onTS(e) {
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
        consumed = false;
      }
      function onTM(e) {
        if (consumed || animating) return;
        var dx = e.touches[0].clientX - sx;
        var dy = e.touches[0].clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          consumed = true;
          var dir = dx < 0 ? 1 : -1;
          transitionTo(index + dir, dir);
        }
      }
      section.addEventListener('touchstart', onTS, { passive: true });
      section.addEventListener('touchmove', onTM, { passive: true });
      return function () {
        section.removeEventListener('touchstart', onTS);
        section.removeEventListener('touchmove', onTM);
      };
    });

    navItems.forEach(function (item, idx) {
      item.addEventListener('click', function () {
        if (animating || idx === index) return;
        transitionTo(idx, idx > index ? 1 : -1);
      });
    });
  }

  /* ---------------- How are we different carousel ---------------- */
  function initDiff(root) {
    var section = root.querySelector('[data-tr-diff]');
    if (!section || section.dataset.trInit) return;
    section.dataset.trInit = '1';
    var track = section.querySelector('[data-tr-track]');
    var prev = section.querySelector('[data-tr-prev]');
    var next = section.querySelector('[data-tr-next]');
    if (!track) return;

    function step() {
      var slide = track.querySelector('.tr-diff__slide');
      if (!slide) return 600;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }
    function updateArrows() {
      if (!prev || !next) return;
      var max = track.scrollWidth - track.clientWidth - 2;
      prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      next.toggleAttribute('disabled', track.scrollLeft >= max);
    }

    /* GSAP pinned horizontal scroll (desktop): the section pins once it is
       centred in the viewport and vertical scroll drives the cards
       horizontally, 1px of page scroll per 1px of track, until the last
       card — then the pin releases. Mobile keeps native swiping. */
    var horizontalST = null;
    if (hasGsap && !prefersReduced && !(window.Shopify && window.Shopify.designMode)) {
      var mm = gsap.matchMedia();
      mm.add(SQUEEZE, function () {
        var scroller = document.querySelector('.page-wrapper') || window;
        /* CSS scroll-snap fights the per-frame scrollLeft writes of the
           scrub (the browser keeps re-snapping mid-scrub = judder) —
           snap is only for native swiping, so drop it while pinned */
        track.style.scrollSnapType = 'none';
        var proxy = { v: track.scrollLeft };
        var tween = gsap.to(proxy, {
          v: function () { return Math.max(0, track.scrollWidth - track.clientWidth); },
          ease: 'none',
          onUpdate: function () { track.scrollLeft = proxy.v; },
          scrollTrigger: {
            trigger: section,
            scroller: scroller,
            start: 'center center',
            end: function () { return '+=' + Math.max(1, track.scrollWidth - track.clientWidth); },
            pin: true,
            anticipatePin: 1,
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });
        horizontalST = tween.scrollTrigger;
        return function () {
          track.style.scrollSnapType = '';
          if (tween.scrollTrigger) tween.scrollTrigger.kill();
          tween.kill();
          horizontalST = null;
        };
      });
    }

    function arrowScroll(dir) {
      if (horizontalST) {
        /* while pinned, page scroll IS the horizontal position (1:1) */
        var scroller = getScroller();
        var target = horizontalST.start + Math.min(
          horizontalST.end - horizontalST.start,
          Math.max(0, track.scrollLeft + dir * step())
        );
        scrollerToY(scroller, target, { duration: 0.7 });
      } else {
        track.scrollBy({ left: dir * step(), behavior: 'smooth' });
      }
    }
    if (prev) prev.addEventListener('click', function () { arrowScroll(-1); });
    if (next) next.addEventListener('click', function () { arrowScroll(1); });
    track.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();

    /* drag to scroll on desktop */
    var isDown = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true; moved = false;
      startX = e.clientX; startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
    });
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }

  /* ---------------- Why-choose fade carousel ---------------- */
  function initWhyChoose(root) {
    var section = root.querySelector('[data-tr-why]');
    if (!section || section.dataset.trInit) return;
    section.dataset.trInit = '1';
    var slides = [].slice.call(section.querySelectorAll('[data-tr-why-slide]'));
    var dots = [].slice.call(section.querySelectorAll('[data-tr-why-dots] span'));
    var prev = section.querySelector('[data-tr-why-prev]');
    var next = section.querySelector('[data-tr-why-next]');
    if (slides.length < 2) return;
    var idx = 0;
    var timer = null;

    function show(i) {
      idx = (i + slides.length) % slides.length;
      slides.forEach(function (s, j) { s.classList.toggle('is-active', j === idx); });
      dots.forEach(function (d, j) { d.classList.toggle('is-active', j === idx); });
    }
    function schedule() {
      clearInterval(timer);
      timer = setInterval(function () { show(idx + 1); }, 5000);
    }
    /* mobile: features strip becomes a continuously sliding marquee */
    var features = section.querySelector('.tr-why__features');
    if (features && !features.dataset.trMarq && window.matchMedia('(max-width: 749px)').matches) {
      features.dataset.trMarq = '1';
      var track = document.createElement('div');
      track.className = 'tr-why__marqtrack';
      while (features.firstChild) track.appendChild(features.firstChild);
      features.appendChild(track);
      features.appendChild(track.cloneNode(true));
      features.classList.add('is-marquee');
    }
    if (prev) prev.addEventListener('click', function () { show(idx - 1); schedule(); });
    if (next) next.addEventListener('click', function () { show(idx + 1); schedule(); });
    /* mobile has no arrows — dots are tappable and the stage swipes */
    dots.forEach(function (d, j) {
      d.addEventListener('click', function () { show(j); schedule(); });
    });
    var stage = section.querySelector('.tr-why__stage');
    if (stage) {
      /* decide horizontal intent early, then own the gesture: prevent the
         page from scrolling and fire on touchend from the total travel */
      var sx = 0, sy = 0, lx = 0, ly = 0, horiz = false, decided = false;
      stage.addEventListener('touchstart', function (e) {
        sx = lx = e.touches[0].clientX;
        sy = ly = e.touches[0].clientY;
        horiz = false; decided = false;
      }, { passive: true });
      stage.addEventListener('touchmove', function (e) {
        lx = e.touches[0].clientX; ly = e.touches[0].clientY;
        if (!decided) {
          var adx = Math.abs(lx - sx);
          var ady = Math.abs(ly - sy);
          if (adx > 8 || ady > 8) {
            decided = true;
            horiz = adx > ady;
          }
        }
        if (horiz) e.preventDefault();
      }, { passive: false });
      stage.addEventListener('touchend', function () {
        if (!horiz) return;
        var dx = lx - sx;
        if (Math.abs(dx) > 24) {
          show(idx + (dx < 0 ? 1 : -1));
          schedule();
        }
      }, { passive: true });
    }
    schedule();
  }

  /* ---------------- CTA play ---------------- */
  function initCta(root) {
    var section = root.querySelector('[data-tr-cta]');
    if (!section || section.dataset.trInit) return;
    section.dataset.trInit = '1';
    var btn = section.querySelector('[data-tr-play]');
    var card = section.querySelector('[data-tr-cta-card]');
    var src = section.getAttribute('data-video-src');
    if (!btn || !card || !src) {
      if (btn && !src) btn.style.display = 'none';
      return;
    }
    btn.addEventListener('click', function () {
      var video = document.createElement('video');
      video.src = src;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      card.appendChild(video);
      section.classList.add('is-playing');
    });
  }

  /* ---------------- Product page ---------------- */
  function initPdp(root) {
    var section = root.querySelector('[data-tr-pdp]');
    if (!section || section.dataset.trInit) return;
    section.dataset.trInit = '1';

    var allImgs = [].slice.call(section.querySelectorAll('[data-tr-pdp-img]'));
    var allThumbs = [].slice.call(section.querySelectorAll('.tr-pdp__thumb'));
    var prev = section.querySelector('[data-tr-pdp-prev]');
    var next = section.querySelector('[data-tr-pdp-next]');

    /* The tin's dot strip encodes the strength, so each render belongs to one
       strength (carried on data-tr-strength, read out of the image alt). The
       gallery shows only the renders for the selected pill; untagged images
       belong to every strength so an un-retagged product still has a gallery. */
    var imgs = allImgs;
    var thumbs = allThumbs;
    var gi = 0;

    function showImg(i) {
      if (!imgs.length) return;
      gi = (i + imgs.length) % imgs.length;
      /* clear across the full set, not just the visible slice — otherwise a
         filtered-out image keeps is-active and would show if the hide fails */
      allImgs.forEach(function (im) { im.classList.remove('is-active'); });
      allThumbs.forEach(function (t) { t.classList.remove('is-active'); });
      imgs[gi].classList.add('is-active');
      if (thumbs[gi]) thumbs[gi].classList.add('is-active');
    }

    function matchesStrength(el, strength) {
      var own = (el.getAttribute('data-tr-strength') || '').toUpperCase();
      return !own || !strength || own === strength.toUpperCase();
    }

    var lastStrength;
    function filterGallery(strength) {
      /* only re-narrow when the strength itself changed — a pack-size click
         must not throw the shopper back to the first image */
      if (strength === lastStrength) return;
      lastStrength = strength;
      var nextImgs = allImgs.filter(function (im) { return matchesStrength(im, strength); });
      if (!nextImgs.length) nextImgs = allImgs;
      var keep = {};
      nextImgs.forEach(function (im) { keep[allImgs.indexOf(im)] = true; });

      imgs = nextImgs;
      thumbs = allThumbs.filter(function (t, j) { return keep[j]; });
      allImgs.forEach(function (im, j) { im.hidden = !keep[j]; });
      allThumbs.forEach(function (t, j) { t.hidden = !keep[j]; });

      var many = imgs.length > 1;
      if (prev) prev.style.display = many ? '' : 'none';
      if (next) next.style.display = many ? '' : 'none';
      showImg(0);
    }

    if (prev) prev.addEventListener('click', function () { showImg(gi - 1); });
    if (next) next.addEventListener('click', function () { showImg(gi + 1); });
    allThumbs.forEach(function (t) {
      t.addEventListener('click', function () { showImg(thumbs.indexOf(t)); });
    });
    if (allImgs.length < 2) {
      if (prev) prev.style.display = 'none';
      if (next) next.style.display = 'none';
    }

    /* variant pills + pack cards */
    var pills = [].slice.call(section.querySelectorAll('.tr-pdp__pill'));
    var priceEl = section.querySelector('[data-tr-pdp-price]');
    var idInput = section.querySelector('[data-tr-pdp-id]');
    var qtyInput = section.querySelector('[data-tr-pdp-qtyinput]');
    var qtyBtns = [].slice.call(section.querySelectorAll('.tr-pdp__qty'));
    var dataEl = section.querySelector('[data-tr-pdp-data]');
    var pdpData = null;
    if (dataEl) {
      try { pdpData = JSON.parse(dataEl.textContent); } catch (e) { pdpData = null; }
    }

    var money = formatMoney;
    /* remember each CTA's own label so an unavailable variant can swap it out
       and swap it back without losing "Add to Cart" vs "Buy Now" */
    var ctas = [].slice.call(section.querySelectorAll('.tr-pdp__cta'));
    ctas.forEach(function (b) { b.dataset.trLabel = b.textContent.trim(); });

    if (pdpData) {
      /* Strength x Pack Size products: a pill + card pair maps to ONE variant,
         cart quantity stays 1 (packs carry their own discounted price). */
      var state = { strength: null, pack: null };
      var activePill = pills.find(function (p) { return p.classList.contains('is-active'); });
      var activeQty = qtyBtns.find(function (b) { return b.classList.contains('is-active'); });
      if (activePill) state.strength = activePill.getAttribute('data-tr-strength');
      if (activeQty) state.pack = activeQty.getAttribute('data-tr-pack');

      function findVariant(strength, pack) {
        return pdpData.variants.find(function (v) {
          return v.opts[pdpData.sIdx] === strength && v.opts[pdpData.pIdx] === pack;
        });
      }
      function sync() {
        var v = findVariant(state.strength, state.pack);
        filterGallery(state.strength);

        /* A combination with no variant, or a sold-out one, must not leave the
           previous variant id sitting in the form — that would add something
           the customer was never shown. Clear it and block the CTAs instead. */
        var sellable = !!v && v.available !== false;
        if (idInput) idInput.value = v ? v.id : '';
        if (qtyInput) qtyInput.value = '1';
        ctas.forEach(function (btn) {
          btn.disabled = !sellable;
          btn.textContent = sellable ? btn.dataset.trLabel : 'Unavailable';
        });
        if (priceEl) priceEl.textContent = v ? money(v.price) : '—';

        qtyBtns.forEach(function (b) {
          var pv = findVariant(state.strength, b.getAttribute('data-tr-pack'));
          var n = parseInt(b.getAttribute('data-qty'), 10) || 1;
          var el = b.querySelector('[data-qty-price]');
          var ok = !!pv && pv.available !== false;
          b.classList.toggle('is-unavailable', !ok);
          b.setAttribute('aria-disabled', ok ? 'false' : 'true');
          if (el) el.textContent = !pv ? '—' : (n > 1 ? money(pv.price / n) + '/each' : money(pv.price));
        });

        pills.forEach(function (p) {
          var s = p.getAttribute('data-tr-strength');
          var any = pdpData.variants.some(function (x) {
            return x.opts[pdpData.sIdx] === s && x.available !== false;
          });
          p.classList.toggle('is-unavailable', !any);
        });
      }
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          if (pill.disabled) return;
          pills.forEach(function (p) { p.classList.remove('is-active'); });
          pill.classList.add('is-active');
          state.strength = pill.getAttribute('data-tr-strength');
          sync();
        });
      });
      qtyBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          qtyBtns.forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');
          state.pack = btn.getAttribute('data-tr-pack');
          sync();
        });
      });
      sync();
    } else {
      /* single-option products / designed placeholder */
      function currentPrice() {
        var active = pills.find(function (p) { return p.classList.contains('is-active'); });
        return active ? parseFloat((active.getAttribute('data-price') || '').replace(/,/g, '')) : NaN;
      }
      function refreshQtyPrices() {
        var p = currentPrice();
        if (isNaN(p)) return;
        qtyBtns.forEach(function (b) {
          var n = parseInt(b.getAttribute('data-qty'), 10) || 1;
          var el = b.querySelector('[data-qty-price]');
          if (el) el.textContent = money(Math.round(p * n * 100));
        });
      }
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          if (pill.disabled) return;
          pills.forEach(function (p) { p.classList.remove('is-active'); });
          pill.classList.add('is-active');
          if (priceEl && pill.getAttribute('data-price')) priceEl.textContent = pill.getAttribute('data-price');
          if (idInput && pill.getAttribute('data-variant-id')) idInput.value = pill.getAttribute('data-variant-id');
          refreshQtyPrices();
        });
      });
      qtyBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          qtyBtns.forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');
          if (qtyInput) qtyInput.value = btn.getAttribute('data-qty') || '1';
        });
      });
    }
  }

  /* ---------- swipe-position dots for mobile carousels ---------- */
  function initBlogDots(root) {
    ['.tr-blog__grid', '.tr-related__grid', '.tr-consume__steps-mob'].forEach(function (sel) {
      var grid = root.querySelector(sel);
      if (!grid || grid.dataset.trDots) return;
      grid.dataset.trDots = '1';
      var cards = [].slice.call(grid.children);
      if (cards.length < 2) return;
      var dots = document.createElement('div');
      dots.className = 'tr-blog__dots';
      cards.forEach(function (c, i) {
        var d = document.createElement('span');
        if (i === 0) d.className = 'is-active';
        dots.appendChild(d);
      });
      grid.parentNode.insertBefore(dots, grid.nextSibling);
      var marks = [].slice.call(dots.children);
      function update() {
        var step = cards[0].offsetWidth + (parseFloat(getComputedStyle(grid).columnGap) || 0);
        var i = Math.min(cards.length - 1, Math.max(0, Math.round(grid.scrollLeft / step)));
        marks.forEach(function (m, idx) { m.classList.toggle('is-active', idx === i); });
      }
      grid.addEventListener('scroll', update, { passive: true });
    });
  }

  /* ---------------- Scroll reveals ---------------- */
  function initReveals(root) {
    if (!hasGsap || prefersReduced) return;
    /* mobile: no scroll reveals — they risk leaving in-viewport sections
       stuck invisible; content shows immediately instead */
    if (window.matchMedia('(max-width: 989px)').matches) return;
    var els = [].slice.call(root.querySelectorAll('[data-tr-reveal]:not([data-tr-revealed])'));
    /* Only arm elements still below the fold — a page restored mid-scroll
       must not end up with already-passed sections stuck invisible. */
    els = els.filter(function (el) {
      return el.getBoundingClientRect().top > window.innerHeight * 0.82;
    });
    if (!els.length) return;
    els.forEach(function (el) { el.setAttribute('data-tr-revealed', '1'); });
    gsap.set(els, { opacity: 0, y: 24 });

    var mm = gsap.matchMedia();
    function build(scroller) {
      var triggers = ScrollTrigger.batch(els, {
        scroller: scroller,
        start: 'top 88%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { opacity: 1, y: 0, duration: 1, ease: 'power3.out', stagger: 0.1, overwrite: true });
        },
      });
      return function () { triggers.forEach(function (t) { t.kill(); }); };
    }
    mm.add(SQUEEZE, function () { return build(document.querySelector('.page-wrapper') || window); });
    mm.add('(max-width: 989px)', function () { return build(window); });
  }

  /* ---------------- off-canvas cart drawer ---------------- */
  function initCartDrawer() {
    var drawer = document.querySelector('[data-tr-cartdrawer]');
    if (!drawer || drawer.dataset.trInit) return;
    drawer.dataset.trInit = '1';

    /* The drawer ships inside the header section, i.e. inside .page-wrapper.
       Leaving it there means `inert` on .page-wrapper (set while the drawer is
       open) also disables the drawer itself, and Lenis would sweep it into its
       content wrapper. Hoist it to <body> so it is a true overlay. */
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    var panel = drawer.querySelector('.tr-cartdrawer__panel') || drawer;
    var itemsEl = drawer.querySelector('[data-tr-cart-items]');
    var emptyEl = drawer.querySelector('[data-tr-cart-empty]');
    var footEl = drawer.querySelector('[data-tr-cart-foot]');
    var countEl = drawer.querySelector('[data-tr-cart-count]');
    var subtotalEl = drawer.querySelector('[data-tr-cart-subtotal]');
    var upsellEl = drawer.querySelector('[data-tr-cart-upsell]');
    /* Handles come from the storefront (see snippets/taurr-base.liquid); the
       literal list is only a fallback if that block is missing. */
    var FLAVORS = (window.TAURR && window.TAURR.flavors && window.TAURR.flavors.length)
      ? window.TAURR.flavors
      : ['watermelon-ice', 'doublemint', 'spearmint', 'citrus'];
    var prodCache = {};
    function getProduct(handle) {
      if (prodCache[handle]) return Promise.resolve(prodCache[handle]);
      return fetch('/products/' + handle + '.js')
        .then(function (r) { if (!r.ok) throw new Error(handle); return r.json(); })
        .then(function (p) { prodCache[handle] = p; return p; });
    }
    function packNum(v) { return parseInt(v, 10) || 0; }
    function itemOpts(it) {
      var o = {};
      (it.options_with_values || []).forEach(function (x) { o[x.name] = x.value; });
      return o;
    }

    var money = formatMoney;
    function esc(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function cleanTitle(t) { return t.replace(/^\s*TAURR\s*:\s*/i, ''); }

    function render(cart) {
      var n = cart.item_count;
      if (countEl) countEl.textContent = '(' + n + ')';
      /* header badge */
      document.querySelectorAll('.tr-header__cart').forEach(function (link) {
        var badge = link.querySelector('.tr-header__cart-count');
        if (n > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'tr-header__cart-count';
            link.appendChild(badge);
          }
          badge.textContent = n;
        } else if (badge) {
          badge.remove();
        }
      });
      if (emptyEl) emptyEl.hidden = n > 0;
      if (footEl) footEl.hidden = n === 0;
      /* money() already carries the store's currency symbol — appending the ISO
         code as well produced "$ 14.95 AED" against "$ 14.95" on the lines */
      if (subtotalEl) subtotalEl.textContent = money(cart.items_subtotal_price);
      itemsEl.innerHTML = cart.items.map(function (it, i) {
        var img = it.image ? it.image.replace(/(\.[a-z]{3,4})(\?|$)/i, '_160x$1$2') : '';
        return (
          '<div class="tr-cartdrawer__item" data-line="' + (i + 1) + '">' +
            (img ? '<img class="tr-cartdrawer__img" src="' + esc(img) + '" alt="" width="80" height="80" loading="lazy">' : '') +
            '<div class="tr-cartdrawer__info">' +
              '<p class="tr-display tr-cartdrawer__itemname">' + esc(cleanTitle(it.product_title)) + '</p>' +
              (it.variant_title ? '<p class="tr-body tr-cartdrawer__variant">' + esc(it.variant_title) + '</p>' : '') +
              '<div class="tr-cartdrawer__row">' +
                '<span class="tr-cartdrawer__qty">' +
                  '<button type="button" data-tr-cart-qty="-1" aria-label="Decrease quantity">&minus;</button>' +
                  '<span>' + it.quantity + '</span>' +
                  '<button type="button" data-tr-cart-qty="1" aria-label="Increase quantity">+</button>' +
                '</span>' +
                '<span class="tr-display tr-cartdrawer__price">' + money(it.final_line_price) + '</span>' +
              '</div>' +
            '</div>' +
            '<button type="button" class="tr-cartdrawer__remove" data-tr-cart-remove aria-label="Remove">' +
              '<svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2 2 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
            '</button>' +
          '</div>'
        );
      }).join('');
      renderUpsell(cart);
    }

    /* upsells above the subtotal: pack upgrade (better per-can price)
       + a flavor not yet in the cart */
    function renderUpsell(cart) {
      if (!upsellEl) return;
      if (!cart.item_count) { upsellEl.hidden = true; upsellEl.innerHTML = ''; return; }

      var upLine = null;
      cart.items.some(function (it, i) {
        var o = itemOpts(it);
        if (o['Pack Size'] && packNum(o['Pack Size']) < 5 && o['Strength']) {
          upLine = { it: it, opts: o };
          return true;
        }
        return false;
      });
      var inCart = {};
      cart.items.forEach(function (it) { inCart[it.handle] = true; });
      var recHandle = FLAVORS.filter(function (h) { return !inCart[h]; })[0];
      var firstOpts = itemOpts(cart.items[0]);

      var jobs = [
        upLine ? getProduct(upLine.it.handle).then(function (p) {
          var cur = packNum(upLine.opts['Pack Size']);
          var target = cur === 1 ? 3 : 5;
          var tv = null;
          p.variants.forEach(function (v) {
            if (tv) return;
            var hasStrength = v.options.indexOf(upLine.opts['Strength']) > -1;
            var pack = 0;
            v.options.forEach(function (o) { if (/can/i.test(o)) pack = packNum(o); });
            if (hasStrength && pack === target) tv = v;
          });
          if (!tv) return '';
          var perNow = upLine.it.price / cur;
          var perNew = tv.price / target;
          if (perNew >= perNow) return '';
          return (
            '<div class="tr-cartdrawer__upcard">' +
              '<div class="tr-cartdrawer__upinfo">' +
                '<p class="tr-display tr-cartdrawer__uptitle">Make it ' + target + ' cans</p>' +
                '<p class="tr-body tr-cartdrawer__uptext">' + esc(cleanTitle(upLine.it.product_title)) + ' &middot; ' + money(perNew) + '/can instead of ' + money(perNow) + '/can</p>' +
              '</div>' +
              '<button type="button" class="tr-display tr-cartdrawer__upbtn" data-tr-cart-upgrade data-key="' + esc(upLine.it.key) + '" data-variant="' + tv.id + '" data-qty="' + upLine.it.quantity + '">Upgrade</button>' +
            '</div>'
          );
        }).catch(function () { return ''; }) : Promise.resolve(''),
        recHandle ? getProduct(recHandle).then(function (p) {
          var rv = null;
          var wantStrength = firstOpts['Strength'];
          p.variants.forEach(function (v) {
            if (rv) return;
            var okStrength = !wantStrength || v.options.indexOf(wantStrength) > -1;
            var pack = 0;
            v.options.forEach(function (o) { if (/can/i.test(o)) pack = packNum(o); });
            if (v.available && okStrength && (pack === 1 || pack === 0)) rv = v;
          });
          rv = rv || p.variants[0];
          if (!rv) return '';
          var img = (p.images && p.images[0]) ? p.images[0].replace(/(\.[a-z]{3,4})(\?|$)/i, '_160x$1$2') : '';
          return (
            '<div class="tr-cartdrawer__upcard">' +
              (img ? '<img class="tr-cartdrawer__upimg" src="' + esc(img) + '" alt="" width="44" height="44" loading="lazy">' : '') +
              '<div class="tr-cartdrawer__upinfo">' +
                '<p class="tr-display tr-cartdrawer__uptitle">Try ' + esc(cleanTitle(p.title)) + '</p>' +
                '<p class="tr-body tr-cartdrawer__uptext">' + money(rv.price) + (wantStrength ? ' &middot; ' + esc(wantStrength) : '') + '</p>' +
              '</div>' +
              '<button type="button" class="tr-display tr-cartdrawer__upbtn" data-tr-cart-add-variant="' + rv.id + '">Add</button>' +
            '</div>'
          );
        }).catch(function () { return ''; }) : Promise.resolve(''),
      ];
      Promise.all(jobs).then(function (cards) {
        var html = cards.join('');
        upsellEl.innerHTML = html;
        upsellEl.hidden = html === '';
      });
    }

    /* Any non-cart response (Shopify error body, proxy hiccup) used to reach
       render() and throw on cart.items.map, leaving the drawer blank with no
       explanation. Validate first, and surface a retry instead. */
    function asCart(r) {
      if (!r.ok) throw new Error('cart request failed: ' + r.status);
      return r.json().then(function (data) {
        if (!data || !Array.isArray(data.items)) throw new Error('unexpected cart payload');
        return data;
      });
    }

    function showError() {
      busy(false);
      if (!itemsEl) return;
      itemsEl.innerHTML =
        '<div class="tr-cartdrawer__error">' +
          '<p class="tr-body">We couldn\'t load your cart just now.</p>' +
          '<button type="button" class="tr-display" data-tr-cart-retry>Try again</button>' +
        '</div>';
      if (footEl) footEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
      if (upsellEl) { upsellEl.hidden = true; upsellEl.innerHTML = ''; }
    }

    /* Marks the panel busy so a slow connection doesn't read as a dead button. */
    function busy(on) {
      drawer.classList.toggle('is-busy', !!on);
      if (itemsEl) itemsEl.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    function refresh() {
      busy(true);
      return fetch('/cart.js')
        .then(asCart)
        .then(function (c) { busy(false); render(c); })
        .catch(function (e) { showError(); throw e; });
    }
    function change(line, qty) {
      busy(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty }),
      })
        .then(asCart)
        .then(function (c) { busy(false); render(c); })
        .catch(function () { showError(); });
    }

    /* --- focus management: the panel is a modal dialog, so focus moves into
       it on open, is trapped while it is open, and returns to whatever opened
       it on close. The rest of the page goes inert so it can't be tabbed. --- */
    var lastFocus = null;
    var pageWrapper = document.querySelector('.page-wrapper');
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function panelFocusables() {
      return [].slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    function trapTab(e) {
      if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      var f = panelFocusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (pageWrapper) pageWrapper.setAttribute('inert', '');
      document.addEventListener('keydown', trapTab, true);
      var closeBtn = panel.querySelector('[data-tr-cart-close]');
      if (closeBtn) closeBtn.focus();
      /* render after the panel is up so a slow /cart.js shows the busy state
         inside an open drawer rather than swallowing the click entirely */
      refresh().catch(function () {});
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (pageWrapper) pageWrapper.removeAttribute('inert');
      document.removeEventListener('keydown', trapTab, true);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      lastFocus = null;
    }

    document.addEventListener('click', function (e) {
      var cartLink = e.target.closest('.tr-header__cart');
      if (cartLink) {
        /* cmd/ctrl/shift/middle-click must still reach /cart in a new tab */
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        open();
        return;
      }
      if (e.target.closest('[data-tr-cart-retry]')) { refresh().catch(function () {}); return; }
      if (e.target.closest('[data-tr-cart-close]')) { close(); return; }
      var qtyBtn = e.target.closest('[data-tr-cart-qty]');
      if (qtyBtn && drawer.contains(qtyBtn)) {
        var row = qtyBtn.closest('[data-line]');
        var cur = +row.querySelector('.tr-cartdrawer__qty span').textContent;
        change(+row.dataset.line, Math.max(0, cur + +qtyBtn.dataset.trCartQty));
        return;
      }
      var rm = e.target.closest('[data-tr-cart-remove]');
      if (rm && drawer.contains(rm)) {
        change(+rm.closest('[data-line]').dataset.line, 0);
        return;
      }
      var addBtn = e.target.closest('[data-tr-cart-add-variant]');
      if (addBtn && drawer.contains(addBtn)) {
        addBtn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: +addBtn.dataset.trCartAddVariant, quantity: 1 }),
        })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return refresh(); })
          .catch(function () { addBtn.disabled = false; showError(); });
        return;
      }
      var upBtn = e.target.closest('[data-tr-cart-upgrade]');
      if (upBtn && drawer.contains(upBtn)) {
        upBtn.disabled = true;
        /* add the bigger pack, then drop the old line (by key) */
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: +upBtn.dataset.variant, quantity: +upBtn.dataset.qty || 1 }),
        }).then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: upBtn.dataset.key, quantity: 0 }),
          });
        }).then(refresh);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    /* AJAX add-to-cart -> open the drawer instead of navigating.
       Buy Now (name=checkout / formaction) keeps its native submit. */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.action || form.action.indexOf('/cart/add') === -1) return;
      var sub = e.submitter;
      if (sub && (sub.name === 'checkout' || sub.hasAttribute('data-tr-pdp-buynow'))) return;
      e.preventDefault();
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(open)
        .catch(function () { form.submit(); });
    });
  }

  /* ---------------- cart page ----------------
     Live quantity edits via /cart/change.js. Without JS the qty fields are a
     plain form and the noscript "Update cart" button submits it, so the page
     still works. */
  function initCartPage(root) {
    var page = root.querySelector('[data-tr-cartpage]');
    if (!page || page.dataset.trInit) return;
    page.dataset.trInit = '1';

    function busy(on) { page.classList.toggle('is-busy', !!on); }

    function change(line, qty) {
      busy(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('change failed');
          return r.json();
        })
        .then(function () {
          /* re-render server-side: totals, discounts and the empty state are
             all Liquid, so a reload is both simplest and always correct */
          window.location.reload();
        })
        .catch(function () {
          busy(false);
          window.location.reload();
        });
    }

    page.addEventListener('click', function (e) {
      var step = e.target.closest('[data-tr-cartpage-step]');
      if (step) {
        var row = step.closest('[data-line]');
        var input = row.querySelector('[data-tr-cartpage-qty]');
        var next = Math.max(0, (parseInt(input.value, 10) || 0) + (+step.dataset.trCartpageStep));
        input.value = next;
        change(+row.dataset.line, next);
        return;
      }
      var rm = e.target.closest('[data-tr-cartpage-remove]');
      if (rm) {
        e.preventDefault();
        change(+rm.closest('[data-line]').dataset.line, 0);
      }
    });

    page.addEventListener('change', function (e) {
      var input = e.target.closest('[data-tr-cartpage-qty]');
      if (!input) return;
      var row = input.closest('[data-line]');
      change(+row.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
    });
  }

  /* ---------------- store locator (Leaflet + OSM) ---------------- */
  function initLocator(root) {
    var section = root.querySelector('[data-tr-locmap]');
    if (!section || section.dataset.trInit) return;
    section.dataset.trInit = '1';

    var mapEl = section.querySelector('[data-tr-locmap-map]');
    var dataEl = section.querySelector('[data-tr-locmap-data]');
    var items = [].slice.call(section.querySelectorAll('[data-tr-locmap-store]'));
    var search = section.querySelector('[data-tr-locmap-search]');
    var count = section.querySelector('[data-tr-locmap-count]');
    var empty = section.querySelector('[data-tr-locmap-empty]');
    if (!mapEl || !dataEl) return;

    var stores;
    try { stores = JSON.parse(dataEl.textContent); } catch (err) { return; }
    if (!stores.length) return;

    /* taurr-leaflet.js is a deferred tag rendered by the section — poll
       briefly in case it lands after this script runs */
    var tries = 0;
    (function boot() {
      if (typeof window.L === 'undefined') {
        if (tries++ < 100) setTimeout(boot, 100);
        return;
      }
      build();
    })();

    function build() {
      var map = L.map(mapEl, { scrollWheelZoom: false, zoomControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);
      /* Click to zoom with the wheel, but hand the wheel back when the pointer
         leaves — otherwise one click on a pin traps page scrolling for good. */
      map.on('click', function () { map.scrollWheelZoom.enable(); });
      mapEl.addEventListener('mouseleave', function () { map.scrollWheelZoom.disable(); });

      var pin = L.divIcon({
        className: 'tr-locmap__pin',
        html: '<svg width="34" height="44" viewBox="0 0 34 44"><path d="M17 0C7.6 0 0 7.6 0 17c0 12.7 17 27 17 27s17-14.3 17-27C34 7.6 26.4 0 17 0Z" fill="#D40306"/><circle cx="17" cy="16.5" r="6" fill="#fff"/></svg>',
        iconSize: [34, 44],
        iconAnchor: [17, 44],
        popupAnchor: [0, -44],
      });

      var markers = stores.map(function (s, i) {
        var m = L.marker([s.lat, s.lng], { icon: pin }).addTo(map);
        m.bindPopup(
          '<p class="tr-display tr-locmap__popname">' + escHtml(s.name) + '</p>' +
          '<p class="tr-locmap__popaddr">' + escHtml(s.address) + '</p>' +
          '<a class="tr-display tr-locmap__popdir" target="_blank" rel="noopener" href="https://www.google.com/maps/dir/?api=1&destination=' + s.lat + '%2C' + s.lng + '">Get Directions</a>'
        );
        m.on('click', function () { setActive(i, false); });
        return m;
      });

      function bounds(list) {
        return L.latLngBounds(list.map(function (m) { return m.getLatLng(); }));
      }
      map.fitBounds(bounds(markers), { padding: [40, 40] });

      /* keep the pins framed when the panel changes size (orientation
         change, responsive breakpoint) */
      var rsT = null;
      window.addEventListener('resize', function () {
        clearTimeout(rsT);
        rsT = setTimeout(function () {
          map.invalidateSize();
          var vis = markers.filter(function (m) { return map.hasLayer(m); });
          if (vis.length) map.fitBounds(bounds(vis), { padding: [40, 40], maxZoom: 15 });
        }, 250);
      });

      function setActive(i, fly) {
        items.forEach(function (it) {
          it.classList.toggle('is-active', +it.dataset.idx === i);
        });
        var it = items.filter(function (x) { return +x.dataset.idx === i; })[0];
        if (it && it.scrollIntoView) it.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        if (fly) {
          map.flyTo(markers[i].getLatLng(), Math.max(map.getZoom(), 15), { duration: 0.8 });
          markers[i].openPopup();
        }
      }

      items.forEach(function (it) {
        var go = function () { setActive(+it.dataset.idx, true); };
        it.addEventListener('click', function (e) {
          if (e.target.closest('a')) return; /* let Directions / Call through */
          go();
        });
        it.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
        });
      });

      if (search) {
        search.addEventListener('input', function () {
          var q = search.value.trim().toLowerCase();
          var shown = 0;
          items.forEach(function (it) {
            var i = +it.dataset.idx;
            var hit = !q || (stores[i].name + ' ' + stores[i].address).toLowerCase().indexOf(q) > -1;
            it.hidden = !hit;
            if (hit) { shown++; markers[i].addTo(map); } else { map.removeLayer(markers[i]); }
          });
          if (count) count.textContent = shown + (shown === 1 ? ' store' : ' stores');
          if (empty) empty.hidden = shown > 0;
          var vis = markers.filter(function (m) { return map.hasLayer(m); });
          if (vis.length) map.fitBounds(bounds(vis), { padding: [40, 40], maxZoom: 15 });
        });
      }
    }
  }

  /* ---------------- On the 'Gram live feed ----------------
     The merchant's Instafeed app block only exists where they placed it (the
     homepage), so every other page carrying this section fell back to the five
     curated tiles — the client read that as "Instagram not synched on About
     Us". Instafeed's app embed loads site-wide and its script fills whatever
     #insta-feed container it finds, so those sections now ship an empty
     container next to the tiles and this picks a winner: the live feed as soon
     as it paints, the curated tiles if the app is absent, blocked, or slow. */
  function initInstaFeed(root) {
    var live = root.querySelector('[data-tr-insta-live]');
    if (!live || live.dataset.trInit) return;
    live.dataset.trInit = '1';
    var feed = live.querySelector('#insta-feed');
    var fallback = root.querySelector('[data-tr-insta-fallback]');
    if (!feed) return;

    /* Instafeed only ever fills the first #insta-feed on the page. If the
       merchant also placed the app in its own section, ours is dead markup. */
    if (document.querySelectorAll('#insta-feed')[0] !== feed) {
      live.remove();
      return;
    }

    function showFeed() {
      live.classList.add('is-live');
      if (fallback) fallback.hidden = true;
      if (hasGsap) ScrollTrigger.refresh();
    }
    function showTiles() {
      live.classList.remove('is-live');
      if (fallback) fallback.hidden = false;
    }

    /* The app embed's <script> is in the document long before the feed paints;
       hide the tiles up front on that signal so the two never show at once. */
    if (fallback && document.querySelector('script[src*="nfcube"], script[src*="instafeed"]')) {
      fallback.hidden = true;
    }

    if (feed.children.length) { showFeed(); return; }

    var settled = false;
    function settle(ok) {
      if (settled) return;
      settled = true;
      if (mo) mo.disconnect();
      if (ok) showFeed(); else showTiles();
    }
    var mo = null;
    if (window.MutationObserver) {
      mo = new MutationObserver(function () {
        if (feed.children.length) settle(true);
      });
      mo.observe(feed, { childList: true, subtree: true });
    }
    setTimeout(function () { settle(feed.children.length > 0); }, 8000);
  }

  function initAll(root) {
    initAgeGate(root);
    initHeader(root);
    initHero(root);
    initFlavors(root);
    initDiff(root);
    initWhyChoose(root);
    initPdp(root);
    initCookieConsent(root);
    initCta(root);
    initBlogDots(root);
    initInstaFeed(root);
    initLocator(root);
    initCartPage(root);
    initCartDrawer();
    initReveals(root);
    if (hasGsap) ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
